import { maskPromptPayId, normalizePromptPayId } from '@pos/promptpay';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select } from '../../components/Select';
import {
  listBranches,
  listBranchManualPaymentChannels,
  listBranchPaymentSettings,
  listManualPaymentChannels,
  listPaymentMethods,
} from '../../data/fetchers';
import {
  saveBranchPaymentSettings,
  upsertBranchManualPaymentChannel,
  upsertManualPaymentChannel,
  upsertPaymentMethod,
} from '../../data/mutators';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';
import type {
  Branch,
  BranchManualPaymentChannel,
  BranchPaymentMethodConfig,
  BranchPaymentSettings,
  ManualPaymentChannel,
  PaymentMethodConfig,
  PromptPayPersonalConfig,
} from '../../types/domain';
import { SettingsHeader } from './SettingsHeader';

type PaymentMethodDraft = Pick<PaymentMethodConfig, 'name' | 'active'>;

const STORE_SCOPE = '__store__';

const EMPTY_BRANCHES: Branch[] = [];
const EMPTY_PAYMENT_METHODS: PaymentMethodConfig[] = [];
const DEFAULT_PAYMENT_METHODS: PaymentMethodConfig[] = [
  { id: 'cash', name: 'เงินสด', active: true },
  { id: 'qr_personal', name: 'พร้อมเพย์ส่วนบุคคล', active: true },
  { id: 'reference_manual', name: 'โอนพร้อมอ้างอิง', active: true },
];
const EMPTY_BRANCH_SETTINGS: BranchPaymentSettings[] = [];
const EMPTY_MANUAL_CHANNELS: ManualPaymentChannel[] = [];
const EMPTY_MANUAL_ASSIGNMENTS: BranchManualPaymentChannel[] = [];

function mergeCanonicalPaymentMethods(
  stored: PaymentMethodConfig[],
): PaymentMethodConfig[] {
  const storedById = new Map(stored.map((method) => [method.id, method]));
  const canonical = DEFAULT_PAYMENT_METHODS.map((method) => {
    const merged = { ...method, ...storedById.get(method.id) };
    storedById.delete(method.id);
    return method.id === 'cash' ? { ...merged, active: true } : merged;
  });
  return [...canonical, ...storedById.values()];
}

function createDefaultPromptPayConfig(
  methodLabel: string,
  mode: PromptPayPersonalConfig['mode'] = 'override',
): PromptPayPersonalConfig {
  return {
    mode,
    promptpayId: '',
    promptpayIdType: 'mobile',
    accountName: methodLabel,
    staticQrPayload: '',
  };
}

function normalizeBranchPaymentSettings(
  input: BranchPaymentSettings,
  methods: PaymentMethodConfig[],
): BranchPaymentSettings {
  const methodById = new Map(methods.map((method) => [method.id, method]));
  return {
    ...input,
    methods: input.methods.map((row) => {
      if (row.method !== 'qr_personal') {
        return row.method === 'reference_manual'
          ? {
              ...row,
              requiresEvidence: row.requiresEvidence,
            }
          : row;
      }

      const methodLabel =
        row.displayLabel ||
        methodById.get(row.method)?.name ||
        'พร้อมเพย์ส่วนบุคคล';
      const rawConfig = row.config?.qrPersonal;
      const mode =
        rawConfig?.mode ??
        (row.active
          ? 'override'
          : input.branchId === null
            ? 'disabled'
            : 'inherit');
      return {
        ...row,
        requiresEvidence: false,
        config: {
          qrPersonal:
            mode === 'override'
              ? {
                  ...createDefaultPromptPayConfig(methodLabel),
                  ...rawConfig,
                  mode,
                }
              : { mode },
        },
      };
    }),
  };
}

function buildDefaultBranchSettings(
  branchId: string | null,
  methods: PaymentMethodConfig[],
): BranchPaymentSettings {
  const scopedMethods =
    branchId === null
      ? methods.filter((method) => method.id === 'qr_personal')
      : methods;
  return normalizeBranchPaymentSettings(
    {
      branchId,
      methods: scopedMethods.map((method, index) => ({
        method: method.id,
        displayLabel: method.name,
        active: true,
        requiresReference: method.id === 'reference_manual',
        requiresEvidence: method.id === 'reference_manual',
        sortOrder: index + 1,
        config:
          method.id === 'qr_personal'
            ? {
                qrPersonal: createDefaultPromptPayConfig(
                  method.name,
                  branchId === null ? 'override' : 'inherit',
                ),
              }
            : undefined,
      })),
    },
    methods,
  );
}

function isUsablePromptPaySetting(
  settings: BranchPaymentSettings | undefined,
): boolean {
  const row = settings?.methods.find(
    (method) => method.method === 'qr_personal',
  );
  const config = row?.config?.qrPersonal;
  if (
    !row?.active ||
    config?.mode === 'disabled' ||
    config?.mode === 'inherit' ||
    !config?.accountName?.trim()
  ) {
    return false;
  }
  const value = config.promptpayId?.trim() ?? '';
  return config.promptpayIdType === 'mobile'
    ? /^(?:\+66|66|0)\d{9}$/.test(value)
    : /^\d{13}$/.test(value);
}

function isValidPromptPayConfig(config: PromptPayPersonalConfig): boolean {
  if (
    !config.promptpayId?.trim() ||
    !config.promptpayIdType ||
    !config.accountName?.trim()
  ) {
    return false;
  }
  try {
    return Boolean(
      normalizePromptPayId(config.promptpayId, config.promptpayIdType),
    );
  } catch {
    return false;
  }
}

function promptPayIdError(
  config: PromptPayPersonalConfig | undefined,
): string | undefined {
  const type = config?.promptpayIdType ?? 'mobile';
  try {
    if (config?.promptpayId?.trim()) {
      normalizePromptPayId(config.promptpayId, type);
      return undefined;
    }
  } catch {
    // Return the field-specific message below.
  }
  return type === 'national_id'
    ? 'กรอกเลขบัตรประชาชน 13 หลัก'
    : 'กรอกเบอร์โทรศัพท์พร้อมเพย์ให้ถูกต้อง เช่น 0812345678';
}

export function PaymentMethodsView() {
  const queryClient = useQueryClient();
  const sessionUser = useSession((state) => state.user);
  const canEdit = sessionUser ? canEditMasterData(sessionUser) : false;
  const selectedBranchId = useUi((state) => state.branchId);
  const { data: branches = EMPTY_BRANCHES } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const { data: storedPaymentMethodsData } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: listPaymentMethods,
  });
  const usingDefaultPaymentMethods = storedPaymentMethodsData?.length === 0;
  const paymentMethods = useMemo(
    () =>
      storedPaymentMethodsData === undefined
        ? EMPTY_PAYMENT_METHODS
        : mergeCanonicalPaymentMethods(storedPaymentMethodsData),
    [storedPaymentMethodsData],
  );
  const { data: storedBranchSettingsData } = useQuery({
    queryKey: ['branch-payment-settings'],
    queryFn: listBranchPaymentSettings,
  });
  const branchSettings = storedBranchSettingsData ?? EMPTY_BRANCH_SETTINGS;
  const { data: manualChannels = EMPTY_MANUAL_CHANNELS } = useQuery({
    queryKey: ['manual-payment-channels'],
    queryFn: listManualPaymentChannels,
  });
  const { data: manualAssignments = EMPTY_MANUAL_ASSIGNMENTS } = useQuery({
    queryKey: ['branch-manual-payment-channels'],
    queryFn: listBranchManualPaymentChannels,
  });

  const availableBranchId =
    selectedBranchId &&
    branches.some((branch) => branch.id === selectedBranchId)
      ? selectedBranchId
      : branches[0]?.id;
  const [editingScope, setEditingScope] = useState<string>('');
  const [methodDrafts, setMethodDrafts] = useState<
    Record<string, PaymentMethodDraft>
  >({});
  const [branchDraft, setBranchDraft] = useState<BranchPaymentSettings | null>(
    null,
  );
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualName, setManualName] = useState('');
  const [newChannelId, setNewChannelId] = useState('');
  const [newChannelBranch, setNewChannelBranch] = useState('');
  const [channelNotice, setChannelNotice] = useState('');
  const channelNoticeRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (!channelNotice || showManualForm) return;
    const frame = requestAnimationFrame(() => {
      channelNoticeRef.current?.focus({ preventScroll: true });
      channelNoticeRef.current?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [channelNotice, showManualForm]);
  const [manualLabelDrafts, setManualLabelDrafts] = useState<
    Record<string, string>
  >({});
  const [pendingManualArchive, setPendingManualArchive] =
    useState<ManualPaymentChannel | null>(null);

  useEffect(() => {
    if (!availableBranchId) {
      return;
    }
    setEditingScope((current) => current || availableBranchId);
  }, [availableBranchId]);

  useEffect(() => {
    setMethodDrafts((current) => {
      const next: Record<string, PaymentMethodDraft> = {};
      for (const method of paymentMethods) {
        next[method.id] = current[method.id] ?? {
          name: method.name,
          active: method.active,
        };
      }
      return next;
    });
  }, [paymentMethods]);

  useEffect(() => {
    if (
      !editingScope ||
      storedBranchSettingsData === undefined ||
      storedPaymentMethodsData === undefined
    ) {
      return;
    }
    const branchId = editingScope === STORE_SCOPE ? null : editingScope;
    setBranchDraft((current) => {
      if (current?.branchId === branchId) {
        return normalizeBranchPaymentSettings(current, paymentMethods);
      }
      return normalizeBranchPaymentSettings(
        branchSettings.find((row) => row.branchId === branchId) ??
          buildDefaultBranchSettings(branchId, paymentMethods),
        paymentMethods,
      );
    });
  }, [
    editingScope,
    paymentMethods,
    storedBranchSettingsData,
    storedPaymentMethodsData,
  ]);

  const effectivePaymentMethods = useMemo(
    () =>
      paymentMethods.map((method) => ({
        ...method,
        ...methodDrafts[method.id],
        active:
          method.id === 'cash'
            ? true
            : (methodDrafts[method.id]?.active ?? method.active),
      })),
    [methodDrafts, paymentMethods],
  );

  const paymentMethodMutation = useMutation({
    mutationFn: async () => {
      const writes = effectivePaymentMethods.map((method) =>
        upsertPaymentMethod({
          id: method.id,
          name: method.name,
          active: method.active,
        }),
      );
      return Promise.all(writes);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
  });

  const branchSettingsMutation = useMutation({
    mutationFn: (input: BranchPaymentSettings) =>
      saveBranchPaymentSettings(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['branch-payment-settings'],
      });
    },
  });

  const manualChannelMutation = useMutation({
    mutationFn: upsertManualPaymentChannel,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['manual-payment-channels'],
      });
    },
  });
  const manualAssignmentMutation = useMutation({
    mutationFn: upsertBranchManualPaymentChannel,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['branch-manual-payment-channels'],
      });
    },
  });

  const branchRows = useMemo(() => branchDraft?.methods ?? [], [branchDraft]);
  const isStoreScope = editingScope === STORE_SCOPE;
  const currentBranchId = isStoreScope ? null : editingScope || null;
  const currentBranch = branches.find(
    (branch) => branch.id === currentBranchId,
  );
  const gatewayRows = branchRows.filter((row) => row.method === 'qr_gateway');
  const activeManualChannels = manualChannels.filter(
    (channel) => channel.active,
  );
  const branchManualAssignments = currentBranchId
    ? manualAssignments.filter(
        (assignment) => assignment.branchId === currentBranchId,
      )
    : [];
  const qrRow = branchRows.find((row) => row.method === 'qr_personal');
  const promptPayMode =
    qrRow?.config?.qrPersonal?.mode ??
    (isStoreScope ? 'override' : qrRow?.active ? 'override' : 'inherit');
  const promptPayConfig = qrRow?.config?.qrPersonal;
  const promptPayStatusLabel =
    promptPayMode === 'disabled'
      ? 'ไม่รับพร้อมเพย์'
      : promptPayMode === 'inherit'
        ? 'ใช้ค่าทั้งร้าน'
        : 'ใช้บัญชีนี้';
  const promptPayIdType = promptPayConfig?.promptpayIdType ?? 'mobile';
  const promptPayIdLabel =
    promptPayIdType === 'national_id'
      ? 'เลขบัตรประชาชน'
      : 'เบอร์โทรศัพท์พร้อมเพย์';
  const storePromptPay = branchSettings
    .find((settings) => settings.branchId === null)
    ?.methods.find((method) => method.method === 'qr_personal')
    ?.config?.qrPersonal;
  const effectivePromptPay =
    promptPayMode === 'disabled'
      ? null
      : promptPayMode === 'inherit'
        ? storePromptPay
        : qrRow?.config?.qrPersonal;
  const isInheritingStoreDefault =
    !isStoreScope &&
    promptPayMode === 'inherit' &&
    Boolean(editingScope) &&
    !isUsablePromptPaySetting(branchDraft ?? undefined) &&
    isUsablePromptPaySetting(
      branchSettings.find((settings) => settings.branchId === null),
    );
  const canSaveScope =
    Boolean(branchDraft) &&
    branchRows.every(
      (row) =>
        row.method !== 'qr_personal' ||
        !row.active ||
        promptPayMode !== 'override' ||
        isUsablePromptPaySetting(branchDraft ?? undefined),
    );

  const setMethodDraft = (
    methodId: PaymentMethodConfig['id'],
    patch: Partial<PaymentMethodDraft>,
  ) => {
    paymentMethodMutation.reset();
    setMethodDrafts((current) => ({
      ...current,
      [methodId]: (() => {
        const existing = current[methodId];
        const source = paymentMethods.find((method) => method.id === methodId);
        return {
          ...existing,
          name: patch.name ?? existing?.name ?? source?.name ?? '',
          active: patch.active ?? existing?.active ?? source?.active ?? true,
        };
      })(),
    }));
  };

  const setBranchMethod = (
    methodId: BranchPaymentMethodConfig['method'],
    patch: Partial<BranchPaymentMethodConfig>,
  ) => {
    branchSettingsMutation.reset();
    setBranchDraft((current) =>
      current
        ? {
            ...current,
            methods: current.methods.map((method) =>
              method.method === methodId ? { ...method, ...patch } : method,
            ),
          }
        : current,
    );
  };

  const setQrPersonalConfig = (
    patch: Partial<PromptPayPersonalConfig>,
  ): void => {
    branchSettingsMutation.reset();
    setBranchDraft((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        methods: current.methods.map((row) => {
          if (row.method !== 'qr_personal') {
            return row;
          }
          const qrPersonal =
            row.config?.qrPersonal ??
            createDefaultPromptPayConfig(row.displayLabel);
          return {
            ...row,
            requiresEvidence: false,
            config: {
              qrPersonal: {
                ...qrPersonal,
                ...patch,
                mode: 'override',
              },
            },
          };
        }),
      };
    });
  };

  const setPromptPayMode = (
    mode: NonNullable<PromptPayPersonalConfig['mode']>,
  ) => {
    branchSettingsMutation.reset();
    setBranchDraft((current) =>
      current
        ? {
            ...current,
            methods: current.methods.map((row) =>
              row.method === 'qr_personal'
                ? {
                    ...row,
                    active: mode !== 'disabled',
                    config: {
                      qrPersonal:
                        mode === 'override'
                          ? {
                              ...createDefaultPromptPayConfig(row.displayLabel),
                              ...(row.config?.qrPersonal ?? {}),
                              mode,
                            }
                          : { mode },
                    },
                  }
                : row,
            ),
          }
        : current,
    );
  };

  const createChannelMutation = useMutation({
    mutationFn: async (input: {
      channel: ManualPaymentChannel;
      branchId: string;
    }) => {
      await upsertManualPaymentChannel(input.channel);
      if (input.branchId) {
        await upsertBranchManualPaymentChannel({
          branchId: input.branchId,
          channelId: input.channel.id,
          displayLabel: input.channel.name,
          active: true,
          sortOrder: activeManualChannels.length + 1,
        });
      }
      return input;
    },
    onSuccess: (input) => {
      setChannelNotice(
        input.branchId
          ? `เพิ่ม ${input.channel.name} และตั้งค่าให้ใช้ที่ ${branches.find((branch) => branch.id === input.branchId)?.short ?? 'สาขาที่เลือก'} แล้ว`
          : `เพิ่ม ${input.channel.name} แล้ว — เลือกสาขาเพื่อเปิดใช้ช่องทางนี้`,
      );
      setShowManualForm(false);
      setManualName('');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['manual-payment-channels'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['branch-manual-payment-channels'],
      });
    },
  });

  const createManualChannel = () => {
    if (!manualName.trim()) return;
    createChannelMutation.mutate({
      channel: { id: newChannelId, name: manualName.trim(), active: true },
      branchId: newChannelBranch,
    });
  };

  const saveManualAssignment = (
    channel: ManualPaymentChannel,
    active: boolean,
  ) => {
    if (!currentBranchId) return;
    const existing = branchManualAssignments.find(
      (assignment) => assignment.channelId === channel.id,
    );
    manualAssignmentMutation.mutate({
      branchId: currentBranchId,
      channelId: channel.id,
      displayLabel:
        manualLabelDrafts[channel.id] ?? existing?.displayLabel ?? channel.name,
      active,
      sortOrder:
        existing?.sortOrder ?? activeManualChannels.indexOf(channel) + 1,
    });
  };

  if (!canEdit) {
    return (
      <div className="bo-fade">
        <SettingsHeader
          title="วิธีชำระเงิน"
          subtitle="ตั้งค่าบัญชีรับเงินและช่องทางชำระของแต่ละสาขา"
          status={{ label: 'จำกัดสิทธิ์ผู้จัดการขึ้นไป', tone: 'neutral' }}
        />
        <div className="bo-card">
          <EmptyState
            title="เฉพาะผู้จัดการขึ้นไป"
            sub="ให้ผู้จัดการหรือเจ้าของร้านตั้งค่าช่องทางรับเงิน"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bo-fade">
      <SettingsHeader
        title="วิธีชำระเงิน"
        subtitle="ตั้งค่าบัญชีรับเงินและช่องทางชำระของแต่ละสาขา"
        status={{
          label:
            storedPaymentMethodsData === undefined
              ? 'กำลังโหลดข้อมูล'
              : `${effectivePaymentMethods.filter((method) => method.active).length} ประเภทการรับเงิน`,
          tone: storedPaymentMethodsData === undefined ? 'neutral' : 'ok',
        }}
      />
      <div className="space-y-6">
        <section data-testid="branch-payment-settings">
          <Card title="ช่องทางรับเงินของร้าน">
            <div className="mb-5 flex flex-col gap-3 border-b border-line pb-5 md:flex-row md:items-end md:justify-between">
              <div className="grid gap-2 sm:grid-cols-[minmax(0,220px)_1fr] sm:items-end">
                <Field label="1. เลือกสาขาที่ต้องการตั้งค่า">
                  <Select
                    aria-label="เลือกสาขาวิธีชำระ"
                    value={editingScope}
                    onChange={(event) => {
                      setEditingScope(event.target.value);
                      setManualLabelDrafts({});
                      setChannelNotice('');
                      branchSettingsMutation.reset();
                    }}
                  >
                    <option value={STORE_SCOPE}>ทั้งร้าน (ค่าเริ่มต้น)</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.short}
                      </option>
                    ))}
                  </Select>
                </Field>
                <p className="pb-2 text-sm-label text-ink-3">
                  {isStoreScope
                    ? 'ใช้เป็นค่าเริ่มต้นสำหรับทุกสาขา'
                    : `กำลังตั้งค่าสาขา ${currentBranch?.short ?? ''}`}
                </p>
              </div>
            </div>

            <section className="mb-6">
              <div className="mb-3">
                <h3 className="font-semibold text-ink-1">วิธีที่รับชำระ</h3>
                <p className="text-xs text-ink-3">
                  เงินสดใช้ได้เสมอ ส่วนพร้อมเพย์และช่องทางอื่นตั้งค่าได้ด้านล่าง
                </p>
              </div>
              <div
                data-testid="branch-payment-overview"
                className="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
              >
                <div
                  aria-label={
                    branchRows.some((row) => row.method === 'cash')
                      ? 'display cash'
                      : undefined
                  }
                  className="rounded-card border border-line bg-surface-1 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-ink-1">เงินสด</p>
                      <p className="text-xs text-ink-3">รับชำระหน้าร้าน</p>
                    </div>
                    <span className="rounded-full bg-success-50 px-2.5 py-1 text-xs font-semibold text-success-700">
                      เปิดใช้งานเสมอ
                    </span>
                  </div>
                </div>
                <div className="rounded-card border border-line bg-surface-1 p-4">
                  <p className="font-semibold text-ink-1">พร้อมเพย์ส่วนบุคคล</p>
                  <p className="mt-1 text-sm-label text-ink-3">
                    {promptPayStatusLabel}
                  </p>
                </div>
                {gatewayRows.map((row) => (
                  <div
                    key={row.method}
                    className="rounded-card border border-line bg-surface-1 p-4"
                  >
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <p className="font-semibold text-ink-1">QR นิติ-SCB</p>
                      <label className="flex items-center gap-2 text-sm-label text-ink-2">
                        <input
                          aria-label={`active ${row.method}`}
                          type="checkbox"
                          checked={row.active}
                          onChange={(event) =>
                            setBranchMethod(row.method, {
                              active: event.target.checked,
                            })
                          }
                        />
                        ใช้งาน
                      </label>
                    </div>
                    <Field label="ชื่อที่แสดง">
                      <Input
                        aria-label={`display ${row.method}`}
                        value={row.displayLabel}
                        onChange={(event) =>
                          setBranchMethod(row.method, {
                            displayLabel: event.target.value,
                          })
                        }
                      />
                    </Field>
                  </div>
                ))}
                <div className="rounded-card border border-line bg-surface-1 p-4">
                  <p className="font-semibold text-ink-1">ช่องทางรับเงินอื่น</p>
                  <p className="mt-1 text-sm-label text-ink-3">
                    {isStoreScope
                      ? `${activeManualChannels.length} ช่องทางในร้าน`
                      : `${branchManualAssignments.filter((row) => row.active).length} ช่องทางในสาขา`}
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-6 rounded-card border border-line p-4">
              <div className="mb-3">
                <h3 className="font-semibold text-ink-1">
                  2. รับเงินด้วยพร้อมเพย์
                </h3>
                <p className="text-xs text-ink-3">
                  เลือกว่าจะใช้บัญชีทั้งร้าน บัญชีเฉพาะสาขา หรือปิดใช้งาน
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="ต้องการใช้บัญชีไหน?">
                  <Select
                    aria-label="ต้องการใช้บัญชีไหน?"
                    value={promptPayMode}
                    onChange={(event) =>
                      setPromptPayMode(
                        event.target.value as NonNullable<
                          PromptPayPersonalConfig['mode']
                        >,
                      )
                    }
                  >
                    {!isStoreScope ? (
                      <option value="inherit">ใช้ค่าทั้งร้าน</option>
                    ) : null}
                    <option value="override">ใช้บัญชีนี้</option>
                    <option value="disabled">ไม่รับพร้อมเพย์</option>
                  </Select>
                </Field>
                {effectivePromptPay &&
                isValidPromptPayConfig(effectivePromptPay) ? (
                  <div className="self-end rounded-card bg-success-50 p-3 text-sm-label">
                    {effectivePromptPay.accountName}{' '}
                    {maskPromptPayId(effectivePromptPay.promptpayId ?? '')}
                  </div>
                ) : (
                  <div className="self-end text-sm-label text-ink-3">
                    ไม่แสดงพร้อมเพย์ในสาขานี้
                  </div>
                )}
                {promptPayMode === 'override' ? (
                  <>
                    <Field label="ประเภทหมายเลขพร้อมเพย์" required>
                      <Select
                        aria-label="ประเภทหมายเลขพร้อมเพย์"
                        value={promptPayIdType}
                        onChange={(event) =>
                          setQrPersonalConfig({
                            promptpayIdType: event.target.value as
                              'mobile' | 'national_id',
                          })
                        }
                      >
                        <option value="mobile">เบอร์โทรศัพท์</option>
                        <option value="national_id">เลขบัตรประชาชน</option>
                      </Select>
                    </Field>
                    <Field
                      label={promptPayIdLabel}
                      required
                      error={promptPayIdError(promptPayConfig)}
                    >
                      <Input
                        aria-label={promptPayIdLabel}
                        inputMode="numeric"
                        maxLength={promptPayIdType === 'national_id' ? 13 : 12}
                        placeholder={
                          promptPayIdType === 'national_id'
                            ? 'เลข 13 หลัก'
                            : 'เช่น 0812345678'
                        }
                        value={promptPayConfig?.promptpayId ?? ''}
                        onChange={(event) =>
                          setQrPersonalConfig({
                            promptpayId: event.target.value,
                          })
                        }
                      />
                    </Field>
                    <Field label="ชื่อบัญชีพร้อมเพย์" required>
                      <Input
                        aria-label="ชื่อบัญชีพร้อมเพย์"
                        value={qrRow?.config?.qrPersonal?.accountName ?? ''}
                        onChange={(event) =>
                          setQrPersonalConfig({
                            accountName: event.target.value,
                          })
                        }
                      />
                    </Field>
                  </>
                ) : null}
                {!isStoreScope && promptPayMode !== 'override' ? (
                  <div className="md:col-span-2">
                    <Btn
                      kind="primary"
                      onClick={() => setPromptPayMode('override')}
                    >
                      เพิ่มบัญชีเฉพาะสาขานี้
                    </Btn>
                  </div>
                ) : null}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Btn
                  kind="primary"
                  onClick={() =>
                    branchDraft && branchSettingsMutation.mutate(branchDraft)
                  }
                  disabled={!canSaveScope || branchSettingsMutation.isPending}
                >
                  {isStoreScope ? 'บันทึกค่าเริ่มต้น' : 'บันทึกสาขา'}
                </Btn>
                {branchSettingsMutation.isPending ? (
                  <span role="status">กำลังบันทึก…</span>
                ) : branchSettingsMutation.isError ? (
                  <span role="alert" className="text-danger">
                    บันทึกไม่สำเร็จ กรุณาลองอีกครั้ง
                  </span>
                ) : branchSettingsMutation.isSuccess ? (
                  <span role="status">บันทึกการตั้งค่าแล้ว</span>
                ) : (
                  <span className="text-sm-label text-ink-3">
                    แก้ไขบัญชีแล้วกดบันทึก
                  </span>
                )}
              </div>
              {isInheritingStoreDefault ? (
                <p className="mt-4 rounded-card bg-warning-50 p-3 text-sm-label text-ink-2">
                  สาขานี้ใช้บัญชีพร้อมเพย์ค่าเริ่มต้นของทั้งร้าน
                </p>
              ) : null}
            </section>
            <section className="mb-6 space-y-3 rounded-card border border-line p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="font-semibold text-ink-1">
                    3. ช่องทางรับเงินอื่น
                  </h3>
                  <p className="text-xs text-ink-3">
                    เช่น บัตรสวัสดิการ หรือช่องทางที่พนักงานตรวจสอบการรับเงินเอง
                  </p>
                </div>
                <Btn
                  kind="primary"
                  className="w-full shrink-0 sm:w-auto"
                  disabled={!branchDraft}
                  onClick={() => {
                    setManualName('');
                    setNewChannelId(`manual-${crypto.randomUUID()}`);
                    setNewChannelBranch(currentBranchId ?? '');
                    createChannelMutation.reset();
                    setChannelNotice('');
                    setShowManualForm(true);
                  }}
                >
                  เพิ่มช่องทางรับเงิน
                </Btn>
              </div>
              {channelNotice ? (
                <p
                  ref={channelNoticeRef}
                  tabIndex={-1}
                  role="status"
                  className="rounded-card bg-success-50 p-3"
                >
                  {channelNotice}
                </p>
              ) : null}
              <p className="text-sm-label text-ink-3">
                {isStoreScope
                  ? 'รายการของทั้งร้าน เลือกสาขาด้านบนเพื่อเปิดใช้ช่องทางที่ต้องการ'
                  : 'ติ๊กเพื่อเปิดใช้ในสาขานี้ ระบบบันทึกให้ทันที'}
              </p>
              {manualAssignmentMutation.isError ||
              manualChannelMutation.isError ? (
                <p role="alert" className="text-danger">
                  บันทึกไม่สำเร็จ กรุณาลองอีกครั้ง
                </p>
              ) : null}
              <div className="space-y-2">
                {(isStoreScope
                  ? manualChannels
                  : manualChannels.filter((channel) => !channel.active)
                ).map((channel) => (
                  <div
                    key={channel.id}
                    className="flex items-center justify-between gap-3 rounded-card bg-surface-2 p-2"
                  >
                    <span
                      className={
                        channel.active ? '' : 'text-ink-4 line-through'
                      }
                    >
                      {channel.name}
                    </span>
                    <Btn
                      kind="ghost"
                      onClick={() => {
                        if (channel.active) {
                          setPendingManualArchive(channel);
                          return;
                        }
                        manualChannelMutation.mutate({
                          ...channel,
                          active: true,
                        });
                      }}
                    >
                      {channel.active ? 'เลิกใช้' : 'เปิดใช้อีกครั้ง'}
                    </Btn>
                  </div>
                ))}
              </div>
              {!isStoreScope ? (
                <div
                  data-testid="manual-channel-grid"
                  className="grid gap-3 border-t border-line pt-3 md:grid-cols-2"
                >
                  {activeManualChannels.map((channel) => {
                    const assignment = branchManualAssignments.find(
                      (row) => row.channelId === channel.id,
                    );
                    const label =
                      manualLabelDrafts[channel.id] ??
                      assignment?.displayLabel ??
                      channel.name;
                    return (
                      <div
                        key={channel.id}
                        className="rounded-card border border-line p-3"
                      >
                        <label className="flex items-center gap-2 font-medium text-ink-1">
                          <input
                            aria-label={`ใช้ ${channel.name}`}
                            type="checkbox"
                            disabled={manualAssignmentMutation.isPending}
                            checked={assignment?.active === true}
                            onChange={(event) =>
                              saveManualAssignment(
                                channel,
                                event.target.checked,
                              )
                            }
                          />
                          {channel.name}
                        </label>
                        <p className="mt-2 text-sm-label text-ink-3">
                          {assignment?.active
                            ? 'เปิดใช้ในสาขานี้แล้ว'
                            : 'ยังไม่เปิดใช้ในสาขานี้'}
                        </p>
                        <Btn
                          kind="ghost"
                          onClick={() => setPendingManualArchive(channel)}
                        >
                          เลิกใช้
                        </Btn>
                        {assignment?.active ? (
                          <div className="mt-3">
                            <Field
                              label="ชื่อที่แสดงในสาขา"
                              hint="แก้ชื่อแล้วกดบันทึกชื่อด้านล่าง"
                            >
                              <Input
                                aria-label={`ชื่อสาขา ${channel.name}`}
                                value={label}
                                onChange={(event) =>
                                  setManualLabelDrafts((current) => ({
                                    ...current,
                                    [channel.id]: event.target.value,
                                  }))
                                }
                              />
                            </Field>
                            <Btn
                              className="mt-2"
                              disabled={manualAssignmentMutation.isPending}
                              onClick={() =>
                                saveManualAssignment(channel, true)
                              }
                            >
                              บันทึกชื่อ
                            </Btn>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </section>

            {gatewayRows.length > 0 ? (
              <details className="rounded-card border border-line bg-surface-1 p-4">
                <summary className="cursor-pointer font-semibold text-ink-1">
                  ตั้งค่าขั้นสูง
                </summary>
                <div className="mt-4 space-y-3">
                  {gatewayRows.map((row) => (
                    <div
                      key={row.method}
                      className="grid gap-3 rounded-card bg-surface-2 p-3 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end"
                    >
                      <div>
                        <p className="font-medium text-ink-1">
                          {row.displayLabel}
                        </p>
                        <p className="text-xs text-ink-3">{row.method}</p>
                      </div>
                      <label className="flex items-center gap-2 text-sm-label text-ink-2">
                        <input
                          aria-label={`ref ${row.method}`}
                          type="checkbox"
                          checked={row.requiresReference}
                          onChange={(event) =>
                            setBranchMethod(row.method, {
                              requiresReference: event.target.checked,
                            })
                          }
                        />
                        ต้องมีอ้างอิง
                      </label>
                      <label className="flex items-center gap-2 text-sm-label text-ink-2">
                        <input
                          aria-label={`evidence ${row.method}`}
                          type="checkbox"
                          checked={row.requiresEvidence}
                          onChange={(event) =>
                            setBranchMethod(row.method, {
                              requiresEvidence: event.target.checked,
                            })
                          }
                        />
                        ต้องมีหลักฐาน
                      </label>
                      <Field label="ลำดับ">
                        <Input
                          size="sm"
                          aria-label={`order ${row.method}`}
                          type="number"
                          value={row.sortOrder}
                          onChange={(event) =>
                            setBranchMethod(row.method, {
                              sortOrder: Number(event.target.value),
                            })
                          }
                          className="num w-20"
                        />
                      </Field>
                    </div>
                  ))}
                </div>
              </details>
            ) : null}
          </Card>
        </section>
        <details
          data-testid="global-payment-methods"
          className="rounded-card border border-line p-4"
        >
          <summary className="cursor-pointer font-semibold">
            ตั้งค่าช่องทางของทั้งร้าน
          </summary>
          <Card
            title="วิธีชำระหลัก"
            actions={
              <Btn
                kind="primary"
                onClick={() => paymentMethodMutation.mutate()}
                disabled={paymentMethodMutation.isPending}
              >
                {usingDefaultPaymentMethods
                  ? 'บันทึกช่องทางหลัก'
                  : 'บันทึกชื่อวิธีชำระ'}
              </Btn>
            }
          >
            <p className="mb-4 text-sm-label text-ink-3">
              เปลี่ยนชื่อหรือเปิด/ปิดประเภทการรับเงิน
              การเปลี่ยนแปลงส่วนนี้มีผลกับทั้งร้าน
            </p>
            {usingDefaultPaymentMethods ? (
              <div className="mb-4 rounded-card bg-warning-50 p-3 text-sm-label text-ink-2">
                <strong>ยังไม่มีข้อมูลวิธีชำระในระบบ</strong>
                <span className="ml-1">
                  กดบันทึกช่องทางหลักเพื่อเริ่มตั้งค่าการรับเงินของร้าน
                </span>
              </div>
            ) : null}
            {paymentMethodMutation.isError ? (
              <p role="alert" className="mb-3 text-danger">
                บันทึกไม่สำเร็จ กรุณาลองอีกครั้ง
              </p>
            ) : paymentMethodMutation.isSuccess ? (
              <p role="status" className="mb-3">
                บันทึกการตั้งค่าทั้งร้านแล้ว
              </p>
            ) : null}
            <div
              data-testid="global-payment-method-grid"
              className="grid gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {effectivePaymentMethods.map((method) => {
                const isCash = method.id === 'cash';
                const isManualFamily = method.id === 'reference_manual';
                return (
                  <div
                    key={method.id}
                    className="rounded-card border border-line bg-surface-1 p-4"
                  >
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-ink-1">
                          {method.name}
                        </p>
                      </div>
                      {isCash ? (
                        <span className="rounded-full bg-success-50 px-2.5 py-1 text-xs font-semibold text-success-700">
                          เปิดใช้งานเสมอ
                        </span>
                      ) : (
                        <label className="flex items-center gap-2 text-sm-label text-ink-2">
                          <input
                            aria-label={`เปิดใช้ ${method.id}`}
                            type="checkbox"
                            checked={method.active}
                            onChange={(event) =>
                              setMethodDraft(method.id, {
                                active: event.target.checked,
                              })
                            }
                          />
                          ใช้งาน
                        </label>
                      )}
                    </div>
                    {!isCash ? (
                      <Field label="ชื่อแสดงผล">
                        <Input
                          aria-label={`ชื่อวิธีชำระ ${method.id}`}
                          value={method.name}
                          onChange={(event) =>
                            setMethodDraft(method.id, {
                              name: event.target.value,
                            })
                          }
                        />
                      </Field>
                    ) : null}
                    {isManualFamily ? (
                      <p className="mt-3 text-xs text-ink-3">
                        เพิ่มและเลือกช่องทางรับเงินอื่นได้ในส่วนช่องทางรับเงินอื่นด้านบน
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </Card>
        </details>
      </div>
      <Modal
        open={showManualForm}
        title="เพิ่มช่องทางรับเงิน"
        description="เพิ่มช่องทางที่พนักงานตรวจสอบการรับเงินเอง ระบบไม่ได้เชื่อมต่อผู้ให้บริการรับเงินให้"
        onClose={() => {
          if (!createChannelMutation.isPending) setShowManualForm(false);
        }}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Btn
              disabled={createChannelMutation.isPending}
              onClick={() => setShowManualForm(false)}
            >
              ยกเลิก
            </Btn>
            <Btn
              kind="primary"
              disabled={!manualName.trim() || createChannelMutation.isPending}
              onClick={createManualChannel}
            >
              {createChannelMutation.isPending
                ? 'กำลังบันทึก…'
                : 'บันทึกช่องทาง'}
            </Btn>
          </div>
        }
      >
        <div className="space-y-5">
          <Field
            label="1. ชื่อช่องทางรับเงิน"
            required
            hint="ใช้ชื่อที่พนักงานเข้าใจและหาเจอง่าย"
          >
            <Input
              autoFocus
              aria-label="ชื่อช่องทางรับเงิน"
              placeholder="เช่น บัตรสวัสดิการแห่งรัฐ"
              value={manualName}
              disabled={createChannelMutation.isPending}
              onChange={(event) => setManualName(event.target.value)}
            />
          </Field>
          <Field label="2. ต้องการใช้ที่สาขาไหน?">
            <Select
              aria-label="สาขาที่เปิดใช้ช่องทางใหม่"
              value={newChannelBranch}
              disabled={createChannelMutation.isPending}
              onChange={(event) => setNewChannelBranch(event.target.value)}
            >
              <option value="">เก็บไว้ก่อน ยังไม่เปิดใช้</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.short}
                </option>
              ))}
            </Select>
          </Field>
          <p className="rounded-card bg-surface-2 p-3 text-sm-label">
            {newChannelBranch
              ? 'เมื่อบันทึก จะเพิ่มช่องทางนี้และเปิดใช้ในสาขาที่เลือก หากต้องการใช้สาขาอื่นด้วย ให้เลือกสาขานั้นที่หน้าหลักแล้วติ๊กเปิดใช้'
              : 'เมื่อบันทึก จะเก็บชื่อช่องทางไว้ก่อน ยังไม่เปิดใช้ในสาขาใด'}
          </p>
          {createChannelMutation.isError ? (
            <p role="alert" className="text-danger">
              บันทึกยังไม่ครบ กรุณากดบันทึกอีกครั้ง
              ระบบจะใช้รายการเดิมโดยไม่สร้างซ้ำ
            </p>
          ) : null}
        </div>
      </Modal>
      <Modal
        open={Boolean(pendingManualArchive)}
        title={
          pendingManualArchive
            ? `เลิกใช้ช่องทาง ${pendingManualArchive.name} ?`
            : undefined
        }
        description="ช่องทางนี้จะไม่สามารถเลือกใช้กับสาขาใหม่ได้ แต่ข้อมูลรายการเดิมจะยังอยู่"
        tone="danger"
        onClose={() => setPendingManualArchive(null)}
        footer={
          pendingManualArchive ? (
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Btn
                className="w-full sm:w-auto"
                disabled={manualChannelMutation.isPending}
                onClick={() => setPendingManualArchive(null)}
              >
                ยกเลิก
              </Btn>
              <Btn
                kind="danger"
                className="w-full sm:w-auto"
                disabled={manualChannelMutation.isPending}
                onClick={() => {
                  manualChannelMutation.mutate(
                    { ...pendingManualArchive, active: false },
                    { onSuccess: () => setPendingManualArchive(null) },
                  );
                }}
              >
                {manualChannelMutation.isPending
                  ? 'กำลังเลิกใช้'
                  : 'ยืนยันเลิกใช้'}
              </Btn>
            </div>
          ) : null
        }
      >
        {pendingManualArchive ? (
          <div className="rounded-lg border border-danger/20 bg-danger/5 p-4">
            <div className="font-bold text-danger">ผลหลังเลิกใช้</div>
            <p className="mt-1 text-body text-ink-2">
              การตั้งค่ารายสาขาที่ยังอ้างถึงช่องทางนี้จะไม่ถูกลบ
            </p>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
